import { isNonEmpty, isNotDefined } from '@sgme/fp';
import { FieldValueListLayout } from 'components/shared/FieldValueListNext/FieldValueListLayout';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'components/shared/Intl';
import { FormattedValueWithUnit } from 'components/shared/ValueWithUnit/FormattedValueWithUnit';
import type { SPIntl } from 'contexts/intl/intlContext';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import type { BlotterPanelField } from 'models/blotters/BlotterPanelField';
import type { BlotterItemType } from 'models/blotters/BlotterType';
import type { IBlotterItem } from 'models/IBlotterItem';
import { FC, PropsWithChildren, useMemo } from 'react';
import { camelCaseToWords } from 'utils/string';

import { BlotterPanelUnderlyingsField } from '../Blotter/BlotterPanel/Fields/BlotterPanelUnderlyingsField/BlotterPanelUnderlyingsField';
import { BlotterPanelStatusField } from '../Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { FileCodeLink } from '../FileCodeLink/index';
import { QuoteRepriceLink } from '../QuoteRepriceLink';
import { LinkField } from './LinkField';

export interface FieldValueListNextProps {
  fields: ReadonlyArray<BlotterPanelField>;
  blotterItem: IBlotterItem;
}

const DefaultFieldValueNext: FC<
  PropsWithChildren<{
    field: BlotterPanelField;
    productSubtype: string;
    blotterItem: IBlotterItem;
  }>
> = ({ field, productSubtype, blotterItem }) => {
  const { formatMessages } = useSPIntl();
  const formatedFieldValue = displayFieldByFormat(field, blotterItem, productSubtype, formatMessages);
  return (
    <li
      className="list-group-item border-bottom d-md-flex justify-content-md-between ps-0 pe-3"
      data-e2e="field-value-item"
    >
      <label className="text-secondary mb-0 text-nowrap me-2" data-e2e="field-name">
        <FormattedMessage id={field.label as any} defaultMessage={camelCaseToWords(field.label)} />
      </label>
      <div className="text-truncate" data-e2e="field-value">
        {formatedFieldValue}
      </div>
    </li>
  );
};

const ExternalQuoteIdFieldValueNext: FC<
  PropsWithChildren<{
    field: BlotterPanelField;
    productSubtype: string;
    quoteId: string;
    blotterItem: IBlotterItem;
    disabled: boolean;
  }>
> = ({ field, quoteId, disabled, blotterItem }) => {
  const fieldValue: any = blotterItem[field.key as keyof IBlotterItem];
  return (
    <FieldValueListLayout
      label={<FormattedMessage id={field.label as any} defaultMessage={camelCaseToWords(field.label)} />}
      value={<QuoteRepriceLink quoteId={quoteId} externalQuoteId={fieldValue} disabled={disabled} canCopy={false} />}
    />
  );
};

const FileCodeFieldValueNext: FC<
  PropsWithChildren<{
    field: BlotterPanelField;
    productSubtype: string;
    blotterItem: IBlotterItem;
  }>
> = ({ field, productSubtype, blotterItem }) => {
  const { formatMessages } = useSPIntl();

  const fieldValue = displayFieldByFormat(field, blotterItem, productSubtype, formatMessages);
  const fileCode: any = blotterItem[field.key as keyof IBlotterItem];
  const disabled = fileCode === '';
  return (
    <FieldValueListLayout
      label={<FormattedMessage id={field.label as any} defaultMessage={camelCaseToWords(field.label)} />}
    >
      <FileCodeLink valueToDisplay={fieldValue} fileCode={fileCode} disabled={disabled} />
    </FieldValueListLayout>
  );
};

const FieldValueNext: FC<
  PropsWithChildren<{
    field: BlotterPanelField;
    productSubtype: string;
    quoteId: string;
    blotterItem: IBlotterItem;
    blotterItemType: BlotterItemType | undefined;
  }>
> = ({ field, productSubtype, quoteId, blotterItem, blotterItemType }) => {
  switch (field.key) {
    case 'externalQuoteId':
      return (
        <ExternalQuoteIdFieldValueNext
          field={field}
          quoteId={quoteId}
          productSubtype={productSubtype}
          blotterItem={blotterItem}
          disabled={blotterItemType !== 'Trade' && blotterItemType !== 'Quote'}
        />
      );
    case 'fileCode':
      return <FileCodeFieldValueNext field={field} productSubtype={productSubtype} blotterItem={blotterItem} />;
    default:
      return <DefaultFieldValueNext field={field} productSubtype={productSubtype} blotterItem={blotterItem} />;
  }
};

export const FieldValueListNext: FC<FieldValueListNextProps> = ({ fields, blotterItem }) => {
  const hasFields = isNonEmpty(fields);
  const fieldsToDisplay = useMemo(
    () => fields.filter(summary => blotterItem.hasOwnProperty(summary.key)),
    [blotterItem, fields],
  );

  return (
    <ul className="field-value-list-component list-group list-group-flush" data-e2e="field-value-list">
      {hasFields &&
        fieldsToDisplay.map(field => (
          <FieldValueNext
            key={field.key}
            field={field}
            productSubtype={blotterItem.productSubType}
            quoteId={blotterItem.quoteId}
            blotterItemType={blotterItem.blotterItemType}
            blotterItem={blotterItem}
          />
        ))}
    </ul>
  );
};

const displayValue = (value: unknown): string => {
  const valueToDisplay = typeof value === 'boolean' ? value.toString() : value;
  return !valueToDisplay || valueToDisplay === '' ? '-' : String(valueToDisplay);
};

const displayFieldByFormat = (
  field: BlotterPanelField,
  blotterItem: IBlotterItem,
  productSubType: string,
  formatMessages: SPIntl['formatMessages'],
) => {
  const fieldValue: any = blotterItem[field.key as keyof IBlotterItem];
  if (isNotDefined(fieldValue) || fieldValue === '') {
    return '-';
  }
  switch (field.type) {
    case 'datetime':
      return <FieldListFormattedDatetime value={fieldValue} />;
    case 'date':
      return <FieldListFormattedDate value={fieldValue} />;
    case 'number':
      return <FormattedNumber value={fieldValue || ''} />;
    case 'percent':
      return <FieldListFormattedPercent value={fieldValue || ''} />;
    case 'status':
      return <BlotterPanelStatusField value={fieldValue} productSubType={productSubType} />;
    case 'underlyings':
      const underlyingsToDisplay: string[] = fieldValue.split(';').map((underlying: string) => underlying.trim());
      return <BlotterPanelUnderlyingsField values={underlyingsToDisplay} />;
    case 'creditCheckStatus':
      const creditCheckId = blotterItem.creditCheckId;
      const creditCheckUrl = blotterItem.creditCheckUrl;
      return (
        <>
          <LinkField className="me-3" url={creditCheckUrl} code={creditCheckId} />
          <BlotterPanelStatusField value={fieldValue} />
        </>
      );
    case 'string':
      const value = displayValue(fieldValue);
      const displayedValue = formatMessages({
        fallbackIds: [
          `blotterPanel.${productSubType}.${field.key}.${value}` as IntlKey,
          `blotter.grid.${field.key}.${value}`,
        ],
        defaultMessage: value,
      });
      return <span title={displayedValue}>{displayedValue}</span>;
    case 'boolean':
      if (fieldValue === true) {
        return (
          <>
            <FormattedMessage id="boolean.yes" />
            <em className="icon icon-sm ms-2">check</em>{' '}
          </>
        );
      }
      if (fieldValue === false) {
        return <FormattedMessage id="boolean.no" />;
      }
      return '_';
    case 'valueWithUnit':
      return <FormattedValueWithUnit value={fieldValue} />;
    default:
      return displayValue(fieldValue);
  }
};
interface FieldListFormattedDatetimeProps extends Intl.DateTimeFormatOptions {
  value: string | number | Date;
}
export function FieldListFormattedDatetime({ value, ...props }: FieldListFormattedDatetimeProps) {
  return (
    <FormattedDate
      {...props}
      value={value}
      year="numeric"
      month="long"
      day="2-digit"
      hour="numeric"
      minute="numeric"
      second="numeric"
    />
  );
}
export function FieldListFormattedDate({ value }: { value: string | number | Date }) {
  return <FormattedDate value={value} year="numeric" month="long" day="2-digit" />;
}

export function FieldListFormattedPercent({ value }: { value?: number }) {
  return (
    <span>
      <FormattedNumber value={value} maximumFractionDigits={4} />
      &nbsp;%
    </span>
  );
}
